export type ValidationValueIssue = {
  rowNumber: number;
  value: string;
};

export type ValidationPreviewStatus = 'passed' | 'failed' | 'errored' | 'skipped' | 'untested';

type ValidationDefinition = {
  key: string;
  label: string;
};

type BuildValidationPreviewDataInput = {
  headers: string[];
  rows: string[][];
  headerDefinitions: readonly ValidationDefinition[];
  statusDefinitions: readonly ValidationDefinition[];
  headerValidation: Record<string, 'unknown' | 'valid' | 'invalid'>;
  statusValidation: Record<string, 'unknown' | 'valid' | 'invalid'>;
  draftMapping: Record<string, string>;
  draftStatusAliases: Record<string, string>;
  normalizeHeader: (value: string) => string;
  normalizeStatus: (value: string) => string;
  parseAliasList: (value: string) => string[];
};

export type ValidationPreviewData = {
  matchedHeaders: Set<string>;
  matchedStatuses: Set<string>;
  previewStatusColumnIndexes: number[];
  previewDurationColumnIndexes: number[];
  previewTimestampColumnIndexes: number[];
  totalDurationMs: number | null;
  latestTimestamp: Date | null;
  suiteNames: string[];
  suiteNamesByStatus: Record<ValidationPreviewStatus, string[]>;
  testNames: string[];
  testNamesByStatus: Record<ValidationPreviewStatus, string[]>;
  missingHeaderLabels: string[];
  missingStatusLabels: string[];
  missingTestValues: string[];
  missingSuiteValues: string[];
  invalidStatusValues: string[];
  invalidDurationValues: string[];
  invalidTimestampValues: string[];
  overallStatus: ValidationPreviewStatus;
  suiteCounts: Record<ValidationPreviewStatus, number>;
  testCounts: Record<ValidationPreviewStatus, number>;
  totalSuites: number;
  totalTests: number;
};

const emptyCounts = (): Record<ValidationPreviewStatus, number> => ({
  passed: 0,
  failed: 0,
  errored: 0,
  skipped: 0,
  untested: 0,
});

export const findMatchingColumnIndexes = (
  headers: string[],
  aliases: string[],
  normalizeHeader: (value: string) => string,
): number[] =>
  headers.reduce<number[]>((indexes, header, index) => {
    if (aliases.includes(normalizeHeader(header))) {
      indexes.push(index);
    }
    return indexes;
  }, []);

export const findInvalidColumnValues = (
  rows: string[][],
  columnIndexes: number[],
  isValidValue: (value: string) => boolean,
): ValidationValueIssue[] => {
  const issues: ValidationValueIssue[] = [];
  rows.forEach((row, rowIndex) => {
    columnIndexes.forEach((columnIndex) => {
      const rawValue = row[columnIndex]?.trim() ?? '';
      if (rawValue.length === 0) return;
      if (!isValidValue(rawValue)) {
        issues.push({ rowNumber: rowIndex + 2, value: rawValue });
      }
    });
  });
  return issues;
};

export const isValidDurationValue = (value: string): boolean => {
  return resolveDurationValueMs(value) !== null;
};

export const isValidTimestampValue = (value: string): boolean => !Number.isNaN(Date.parse(value));

export const resolveDurationValueMs = (value: string): number | null => {
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  if (/^\d+(\.\d+)?$/.test(trimmed)) {
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? Math.round(parsed * 1000) : null;
  }
  const unitMatch =
    /^(\d+(?:\.\d+)?)\s*(ms|msec|msecs|millisecond|milliseconds|s|sec|secs|second|seconds|m|min|mins|minute|minutes|h|hr|hrs|hour|hours)$/i.exec(
      trimmed,
    );
  if (unitMatch) {
    const parsed = Number(unitMatch[1]);
    const rawUnit = unitMatch[2];
    if (!rawUnit || !Number.isFinite(parsed)) {
      return null;
    }
    const unit = rawUnit.toLowerCase();
    if (
      unit === 'ms' ||
      unit === 'msec' ||
      unit === 'msecs' ||
      unit === 'millisecond' ||
      unit === 'milliseconds'
    ) {
      return Math.round(parsed);
    }
    if (
      unit === 's' ||
      unit === 'sec' ||
      unit === 'secs' ||
      unit === 'second' ||
      unit === 'seconds'
    ) {
      return Math.round(parsed * 1000);
    }
    if (
      unit === 'm' ||
      unit === 'min' ||
      unit === 'mins' ||
      unit === 'minute' ||
      unit === 'minutes'
    ) {
      return Math.round(parsed * 60 * 1000);
    }
    if (unit === 'h' || unit === 'hr' || unit === 'hrs' || unit === 'hour' || unit === 'hours') {
      return Math.round(parsed * 60 * 60 * 1000);
    }
    return null;
  }
  const parts = trimmed.split(':').map((part) => part.trim());
  if (parts.length !== 2 && parts.length !== 3) {
    return null;
  }
  const numbers = parts.map((part) => Number(part));
  if (numbers.some((num) => !Number.isFinite(num))) {
    return null;
  }
  const [hours, minutes, seconds] = parts.length === 3 ? numbers : [0, numbers[0], numbers[1]];
  if (minutes >= 60 || seconds >= 60 || minutes < 0 || seconds < 0 || hours < 0) {
    return null;
  }
  return Math.round((hours * 3600 + minutes * 60 + seconds) * 1000);
};

const resolveAggregateStatus = (
  counts: Record<ValidationPreviewStatus, number>,
): ValidationPreviewStatus => {
  if (counts.errored > 0) return 'errored';
  if (counts.failed > 0) return 'failed';
  if (counts.passed > 0) return 'passed';
  if (counts.skipped > 0) return 'skipped';
  return 'untested';
};

export const buildValidationPreviewData = ({
  headers,
  rows,
  headerDefinitions,
  statusDefinitions,
  headerValidation,
  statusValidation,
  draftMapping,
  draftStatusAliases,
  normalizeHeader,
  normalizeStatus,
  parseAliasList,
}: BuildValidationPreviewDataInput): ValidationPreviewData => {
  const matchedHeaders = new Set(
    headers.filter((header) =>
      headerDefinitions.some((field) =>
        parseAliasList(draftMapping[field.key])
          .map(normalizeHeader)
          .includes(normalizeHeader(header)),
      ),
    ),
  );

  const missingHeaderLabels = headerDefinitions
    .filter((field) => headerValidation[field.key] === 'invalid')
    .map((field) => field.label);
  const missingStatusLabels = statusDefinitions
    .filter((field) => statusValidation[field.key] === 'invalid')
    .map((field) => field.label);

  const statusHeaderAliases = parseAliasList(draftMapping.status).map(normalizeHeader);
  const durationHeaderAliases = parseAliasList(draftMapping.duration).map(normalizeHeader);
  const timestampHeaderAliases = parseAliasList(draftMapping.timestamp).map(normalizeHeader);
  const suiteHeaderAliases = parseAliasList(draftMapping.suite).map(normalizeHeader);
  const testHeaderAliases = parseAliasList(draftMapping.test).map(normalizeHeader);

  const previewStatusColumnIndexes = findMatchingColumnIndexes(
    headers,
    statusHeaderAliases,
    normalizeHeader,
  );
  const durationColumnIndexes = findMatchingColumnIndexes(
    headers,
    durationHeaderAliases,
    normalizeHeader,
  );
  const timestampColumnIndexes = findMatchingColumnIndexes(
    headers,
    timestampHeaderAliases,
    normalizeHeader,
  );
  const suiteColumnIndexes = findMatchingColumnIndexes(
    headers,
    suiteHeaderAliases,
    normalizeHeader,
  );
  const testColumnIndexes = findMatchingColumnIndexes(headers, testHeaderAliases, normalizeHeader);

  const invalidDurationValues = findInvalidColumnValues(
    rows,
    durationColumnIndexes,
    isValidDurationValue,
  ).map(({ rowNumber, value }) => `Row ${rowNumber}: ${value}`);
  const invalidTimestampValues = findInvalidColumnValues(
    rows,
    timestampColumnIndexes,
    isValidTimestampValue,
  ).map(({ rowNumber, value }) => `Row ${rowNumber}: ${value}`);

  const statusAliasMap = new Map<string, ValidationPreviewStatus>();
  statusDefinitions.forEach((field) => {
    parseAliasList(draftStatusAliases[field.key]).forEach((alias) => {
      const normalizedAlias = normalizeStatus(alias);
      if (normalizedAlias.length > 0) {
        statusAliasMap.set(normalizedAlias, field.key as ValidationPreviewStatus);
      }
    });
  });

  const matchedStatuses = new Set<string>();
  const testCounts = emptyCounts();
  const suiteStatusByName = new Map<string, Record<ValidationPreviewStatus, number>>();
  const testNamesByStatus = new Map<ValidationPreviewStatus, string[]>(
    (Object.keys(emptyCounts()) as ValidationPreviewStatus[]).map((status) => [status, []]),
  );
  const missingTestValues: string[] = [];
  const missingSuiteValues: string[] = [];
  const invalidStatusValues: string[] = [];
  let totalDurationMs: number | null = null;
  let latestTimestamp: Date | null = null;

  rows.forEach((row, rowIndex) => {
    const resolvedStatus =
      previewStatusColumnIndexes
        .map((index) => row[index] ?? '')
        .map((value) => value.trim())
        .find((value) => value.length > 0) ?? '';
    const canonicalStatus = statusAliasMap.get(normalizeStatus(resolvedStatus));
    if (resolvedStatus.length > 0 && canonicalStatus) {
      matchedStatuses.add(resolvedStatus);
    }
    if (resolvedStatus.length > 0 && !canonicalStatus) {
      invalidStatusValues.push(`Row ${rowIndex + 2}: ${resolvedStatus}`);
    }
    const effectiveStatus = canonicalStatus ?? ('untested' as ValidationPreviewStatus);
    testCounts[effectiveStatus] += 1;

    const testName =
      testColumnIndexes
        .map((index) => row[index] ?? '')
        .map((value) => value.trim())
        .find((value) => value.length > 0) ?? '';
    if (testName.length > 0) {
      const currentTestNames = testNamesByStatus.get(effectiveStatus) ?? [];
      currentTestNames.push(testName);
      testNamesByStatus.set(effectiveStatus, currentTestNames);
    } else if (testColumnIndexes.length > 0) {
      missingTestValues.push(`Row ${rowIndex + 2}`);
    }

    const suiteName =
      suiteColumnIndexes
        .map((index) => row[index] ?? '')
        .map((value) => value.trim())
        .find((value) => value.length > 0) ?? '';
    if (suiteName.length > 0) {
      const currentCounts = suiteStatusByName.get(suiteName) ?? emptyCounts();
      currentCounts[effectiveStatus] += 1;
      suiteStatusByName.set(suiteName, currentCounts);
    } else if (suiteColumnIndexes.length > 0) {
      missingSuiteValues.push(`Row ${rowIndex + 2}`);
    }

    const durationValue =
      durationColumnIndexes
        .map((index) => row[index] ?? '')
        .map((value) => value.trim())
        .find((value) => value.length > 0) ?? '';
    const durationMs = durationValue.length > 0 ? resolveDurationValueMs(durationValue) : null;
    if (durationMs !== null) {
      totalDurationMs = (totalDurationMs ?? 0) + durationMs;
    }

    const timestampValue =
      timestampColumnIndexes
        .map((index) => row[index] ?? '')
        .map((value) => value.trim())
        .find((value) => value.length > 0) ?? '';
    if (timestampValue.length > 0 && isValidTimestampValue(timestampValue)) {
      const parsedTimestamp = new Date(timestampValue);
      if (latestTimestamp === null || parsedTimestamp.getTime() > latestTimestamp.getTime()) {
        latestTimestamp = parsedTimestamp;
      }
    }
  });

  const suiteCounts = emptyCounts();
  const suiteNamesByStatus = new Map<ValidationPreviewStatus, string[]>(
    (Object.keys(emptyCounts()) as ValidationPreviewStatus[]).map((status) => [status, []]),
  );
  suiteStatusByName.forEach((counts, suiteName) => {
    const aggregateStatus = resolveAggregateStatus(counts);
    suiteCounts[aggregateStatus] += 1;
    const currentSuiteNames = suiteNamesByStatus.get(aggregateStatus) ?? [];
    currentSuiteNames.push(suiteName);
    suiteNamesByStatus.set(aggregateStatus, currentSuiteNames);
  });

  return {
    matchedHeaders: new Set(Array.from(matchedHeaders).map(normalizeHeader)),
    matchedStatuses: new Set(Array.from(matchedStatuses).map(normalizeStatus)),
    previewStatusColumnIndexes,
    previewDurationColumnIndexes: durationColumnIndexes,
    previewTimestampColumnIndexes: timestampColumnIndexes,
    totalDurationMs,
    latestTimestamp,
    suiteNames: Array.from(suiteStatusByName.keys()),
    suiteNamesByStatus: {
      passed: suiteNamesByStatus.get('passed') ?? [],
      failed: suiteNamesByStatus.get('failed') ?? [],
      errored: suiteNamesByStatus.get('errored') ?? [],
      skipped: suiteNamesByStatus.get('skipped') ?? [],
      untested: suiteNamesByStatus.get('untested') ?? [],
    },
    testNames: Array.from(testNamesByStatus.values()).flat(),
    testNamesByStatus: {
      passed: testNamesByStatus.get('passed') ?? [],
      failed: testNamesByStatus.get('failed') ?? [],
      errored: testNamesByStatus.get('errored') ?? [],
      skipped: testNamesByStatus.get('skipped') ?? [],
      untested: testNamesByStatus.get('untested') ?? [],
    },
    missingHeaderLabels,
    missingStatusLabels,
    missingTestValues,
    missingSuiteValues,
    invalidStatusValues,
    invalidDurationValues,
    invalidTimestampValues,
    overallStatus: resolveAggregateStatus(testCounts),
    suiteCounts,
    testCounts,
    totalSuites: Array.from(suiteStatusByName.keys()).length,
    totalTests: rows.length,
  };
};
